import { ArrowRightLeft, CalendarPlus, MonitorPlay } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate } from '../shared/format'
import type { Demo } from '../shared/types'
import { useCancelDemo, useConvertDemo, useCreateDemo, useDemos, useExtendDemo } from './api'

export function DemosPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [status, setStatus] = useState('aktif')
  const list = useDemos(status)
  const create = useCreateDemo()
  const extend = useExtendDemo()
  const convert = useConvertDemo()
  const cancel = useCancelDemo()
  const [createOpen, setCreateOpen] = useState(false)
  const [extendTarget, setExtendTarget] = useState<Demo | null>(null)
  const [convertTarget, setConvertTarget] = useState<Demo | null>(null)
  const [orderId, setOrderId] = useState('')
  const [days, setDays] = useState(7)
  const [form, setForm] = useState({ account_id: 'acc-2', days: 14, notes: '' })

  const fail = (e: unknown) => toast.error(apiErrorMessage(e))

  return (
    <>
      <PageHeader
        title={t('demos.title')}
        description={t('demos.subtitle')}
        actions={can('demos.manage') ? <Button onClick={() => setCreateOpen(true)}>{t('demos.new')}</Button> : null}
      />
      <div className="mb-4 flex gap-2">
        {['aktif', 'expired', ''].map((s) => (
          <Button key={s || 'all'} size="sm" variant={status === s ? 'default' : 'outline'} onClick={() => setStatus(s)}>
            {s === '' ? t('common.all') : s === 'aktif' ? t('demos.statusActive') : t('demos.statusExpired')}
          </Button>
        ))}
      </div>
      <Card>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.isLoading ? (
          <div className="p-4">
            <Skeleton className="h-40" />
          </div>
        ) : (list.data?.length ?? 0) === 0 ? (
          <EmptyState />
        ) : (
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>{t('demos.columns.account')}</TH>
                <TH>{t('demos.columns.status')}</TH>
                <TH className="text-right">{t('demos.columns.ends')}</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {list.data?.map((d) => (
                <TR key={d.id}>
                  <TD>
                    <Link to={`/accounts/${d.account_id}`} className="font-medium hover:underline">
                      {d.account_title}
                    </Link>
                  </TD>
                  <TD>
                    <Badge tone={d.days_left <= 2 ? 'warning' : 'success'}>{d.status}</Badge>
                  </TD>
                  <TD className="text-right tabular-nums">
                    {fmtDate(d.ends_on)} · {t('common.days', { count: d.days_left })}
                  </TD>
                  <TD className="text-right">
                    {can('demos.manage') ? (
                      <div className="inline-flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setExtendTarget(d)}>
                          {t('demos.extend')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setOrderId('')
                            setConvertTarget(d)
                          }}
                        >
                          {t('demos.convert')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={cancel.isPending}
                          onClick={() =>
                            void cancel
                              .mutateAsync({ id: d.id })
                              .then(() => toast.success(t('demos.cancelDone')))
                              .catch(fail)
                          }
                        >
                          {t('demos.cancel')}
                        </Button>
                      </div>
                    ) : null}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        icon={<MonitorPlay />}
        title={t('demos.new')}
        onSubmit={(e) => {
          e.preventDefault()
          void create
            .mutateAsync({ account_id: form.account_id, modules: ['dental'], days: form.days, notes: form.notes })
            .then(() => {
              toast.success(t('common.saved'))
              setCreateOpen(false)
            })
            .catch(fail)
        }}
        footer={<Button type="submit">{t('common.save')}</Button>}
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>{t('demos.fields.accountId')}</Label>
            <Input value={form.account_id} onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label>{t('demos.fields.days')}</Label>
            <Input type="number" value={form.days} onChange={(e) => setForm((f) => ({ ...f, days: Number(e.target.value) }))} />
          </div>
        </div>
      </Modal>
      <Modal
        open={!!extendTarget}
        onOpenChange={(v) => !v && setExtendTarget(null)}
        icon={<CalendarPlus />}
        title={t('demos.extend')}
        footer={
          <Button
            onClick={() =>
              extendTarget &&
              void extend
                .mutateAsync({ id: extendTarget.id, days })
                .then(() => {
                  toast.success(t('common.saved'))
                  setExtendTarget(null)
                })
                .catch(fail)
            }
          >
            {t('common.save')}
          </Button>
        }
      >
        <Input type="number" value={days} onChange={(e) => setDays(Number(e.target.value))} />
      </Modal>
      <Modal
        open={!!convertTarget}
        onOpenChange={(v) => !v && setConvertTarget(null)}
        icon={<ArrowRightLeft />}
        title={t('demos.convert')}
        description={t('demos.convertHint')}
        footer={
          <Button
            disabled={!orderId.trim() || convert.isPending}
            onClick={() =>
              convertTarget &&
              void convert
                .mutateAsync({ id: convertTarget.id, order_id: orderId.trim() })
                .then(() => {
                  toast.success(t('demos.convertDone'))
                  setConvertTarget(null)
                })
                .catch(fail)
            }
          >
            {t('demos.convertConfirm')}
          </Button>
        }
      >
        <div className="space-y-1">
          <Label>{t('demos.orderId')}</Label>
          <Input value={orderId} onChange={(e) => setOrderId(e.target.value)} />
        </div>
      </Modal>
    </>
  )
}
